// Enums
export enum LoyaltyPlanType {
  ByPoints = 1,
  ByUsage = 2
}

export enum RewardType {
  MoneyDiscount = 0,
  FreeDish = 1,
  FreeDessert = 2,
  Custom = 3
}

export enum TransactionStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected'
}

export enum PlanStatus {
  Active = 0,
  Inactive = 1,
  Expired = 2
}

export enum PointOrigin {
  LojaFisica = 1,
  Telefone = 2,
  Delivery = 3,
  Ifood = 4
}

export enum IntegrationType {
  KdoqDelivery = 1,
  Ifood = 2
}

export enum IntegrationStatus {
  Active = 0,
  Inactive = 1,
  Pending = 2
}

export enum SubscriptionStatus {
  Trial = 0,
  Paid = 1,
  Cancelled = 2
}

// Interfaces
export interface Address {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface Restaurant {
  restaurantId: string;
  name: string;
  phone: string;
  email?: string;
  description?: string;
  address?: Address;
  logoUrl?: string;
  createdAt: string;
  updatedAt?: string;
  isActive?: boolean;
  subscriptionStatus?: SubscriptionStatus;
  registrationDate?: string;
  trialDaysRemaining?: number;
}

export interface LoyaltyPlan {
  planId: string;
  restaurantId: string;
  name: string;
  description: string;
  type: LoyaltyPlanType;
  minimumValue: number;
  targetPoints: number;
  rewardType: RewardType;
  rewardDescription: string;
  rewardValue?: number;
  status: PlanStatus;
  activeOrigins?: PointOrigin[];
  pointsPerOrigin?: Record<PointOrigin, number>;
  createdAt: string;
  updatedAt: string;
}

export interface Integration {
  integrationId: string;
  restaurantId: string;
  type: IntegrationType;
  status: IntegrationStatus;
  configuration?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  clientId: string;
  phone: string;
  name?: string;
  email?: string;
  cpf?: string;
  createdAt: string;
  isActive?: boolean;
}

export interface Enrollment {
  enrollmentId: string;
  clientId: string;
  restaurantId: string;
  planId: string;
  currentPoints: number;
  totalPointsEarned: number;
  rewardsRedeemed: number;
  isActive: boolean;
  enrolledAt: string;
  lastActivityAt: string;
}

export interface Transaction {
  transactionId: string;
  restaurantId: string;
  clientId: string;
  planId: string;
  amount: number;
  pointsEarned: number;
  status: TransactionStatus;
  receiptPhotoUrl?: string;
  transactionDate: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  clientName?: string;
  clientCpf?: string;
  clientPhone?: string;
  historyLog?: string[];
  source?: 'LojaFisica' | 'Telefone' | 'Ifood' | 'Delivery' | 'Resgate';
  notes?: string;
}

export interface DashboardData {
  totalEnrolledClients: number;
  pendingTransactions: number;
  transactionsThisMonth: number;
  totalPointsDistributed: number;
  activePlan?: LoyaltyPlan;
  recentTransactions: Transaction[];
}

export interface Employee {
  employeeRestaurantId: string;
  cognitoUserId: string;
  restaurantId: string;
  name: string;
  phone: string;
  email?: string;
  createdAt: string;
  isActive: boolean;
}

export interface CreateEmployeeDto {
  name: string;
  phone: string;
  email?: string;
}

export interface CreateEmployeeResponseDto {
  employee: Employee;
  userAlreadyExists: boolean;
  requiresConfirmation: boolean;
  message?: string;
}

export interface CreateClientDto {
  name: string;
  phone: string;
  cpf: string;
  email?: string;
}

export interface ClientDto {
  clientId: string;
  cognitoUserId: string;
  name: string;
  phone: string;
  email?: string;
  cpf: string;
  createdAt: string;
  isActive: boolean;
}

export interface CreateClientResponseDto {
  client: ClientDto;
  userAlreadyExists: boolean;
  requiresConfirmation: boolean;
  message?: string;
}

// Form DTOs
export interface CreateLoyaltyPlanDto {
  name: string;
  description: string;
  type: LoyaltyPlanType;
  minimumValue: string | number;
  targetPoints: string | number;
  rewardType: RewardType;
  rewardDescription: string;
  rewardValue?: string | number;
  activeOrigins?: PointOrigin[];
  pointsPerOrigin?: Partial<Record<PointOrigin, number>>;
}

export interface UpdateLoyaltyPlanDto {
  name?: string;
  description?: string;
  minimumValue?: number;
  targetPoints?: number;
  rewardType?: RewardType;
  rewardDescription?: string;
  rewardValue?: number;
  expirationDate?: string;
  validityDays?: number;
  activeOrigins?: PointOrigin[];
  pointsPerOrigin?: Partial<Record<PointOrigin, number>>;
}

export interface UpdateRestaurantDto {
  name?: string;
  email?: string;
  description?: string;
  address?: Address;
}

// Auth Types
export interface AuthUser {
  username: string;
  userId: string;
  token: string;
}

